import { useCallback, useState } from 'react';

/**
 * Audit-modal trigger state. Encapsulates the `{ id, name } | null` pattern
 * that every list page repeats when wiring a row-level "Audit trail" action.
 *
 * Usage:
 * ```tsx
 * const audit = useAuditTrigger();
 *
 * // row action
 * { id: 'audit', label: t('K_AUDIT_TITLE', 'Audit trail'),
 *   onClick: () => audit.open(row.id, row.name) }
 *
 * // modal
 * <AuditTrailModal
 *   open={audit.target !== null}
 *   onClose={audit.close}
 *   filter={{ resourceType: AUDIT_RESOURCE.EMPLOYEE, resourceId: audit.target?.id ?? '' }}
 *   subject={audit.target?.name}
 * />
 * ```
 *
 * Optional helper — page code can keep using local `useState` if the
 * indirection is unwelcome.
 */
/**
 * Trigger target shape is intentionally narrow — id is required, name is
 * optional. Callers that don't have a meaningful display label should call
 * `open(id)` instead of falling back to a UUID — the modal omits the subject
 * pill entirely when name is undefined.
 *
 * Polymorphic-launch use cases (passing `resourceType` through the trigger so
 * a single page can audit multiple resource kinds) should widen this when
 * they show up.
 */
export interface AuditTrigger {
  target: { id: string; name?: string } | null;
  open: (id: string, name?: string) => void;
  close: () => void;
}

export function useAuditTrigger(): AuditTrigger {
  const [target, setTarget] = useState<{ id: string; name?: string } | null>(null);
  const open = useCallback((id: string, name?: string) => setTarget({ id, name }), []);
  const close = useCallback(() => setTarget(null), []);
  return { target, open, close };
}
